import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    TextField,
    InputAdornment,
    Alert,
    Snackbar,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    ButtonGroup,
    Tooltip,
    Switch,
    FormControlLabel
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import ScienceIcon from '@mui/icons-material/Science';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import { useTranslation } from 'react-i18next';
import { getAllUsers, updateUserProfile, assignUserToGroup } from '../../../../services/userService';

const GroupAssignment = () => {
    const { t } = useTranslation();
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [confirmDialog, setConfirmDialog] = useState({ open: false, userId: null, groupType: null, userName: '' });
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [showOldUsers, setShowOldUsers] = useState(false);

    const cutoffDate = new Date('2025-10-01');

    useEffect(() => {
        fetchUsers();
    }, [showOldUsers]);

    useEffect(() => {
        const filtered = users.filter(user => {
            const searchLower = searchQuery.toLowerCase();
            const fullName = `${user.firstname} ${user.lastname}`.toLowerCase();
            const email = user.email.toLowerCase();
            return fullName.includes(searchLower) || email.includes(searchLower);
        });
        setFilteredUsers(filtered);
    }, [searchQuery, users]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const usersData = await getAllUsers();

            let unassignedUsers = Array.isArray(usersData)
                ? usersData.filter(user =>
                    !user.isAdmin && (!user.studyGroup || user.studyGroup === 'unassigned')
                  )
                : [];

            if (!showOldUsers) {
                unassignedUsers = unassignedUsers.filter(user => {
                    const userCreatedAt = new Date(user.createdAt);
                    return userCreatedAt >= cutoffDate;
                });
            }

            const sortedUsers = unassignedUsers.sort((a, b) =>
                new Date(b.createdAt) - new Date(a.createdAt)
            );
            setUsers(sortedUsers);
            setFilteredUsers(sortedUsers);
        } catch (error) {
            console.error('Error fetching users:', error);
            setSnackbar({
                open: true,
                message: t('groupAssignment.fetchError') || 'Failed to fetch users. Please try again.',
                severity: 'error'
            });
            setUsers([]);
            setFilteredUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAssignGroup = (userId, groupType, userName) => {
        setConfirmDialog({
            open: true,
            userId,
            groupType,
            userName
        });
    };

    const confirmAssignment = async () => {
        const { userId, groupType } = confirmDialog;
        try {
            // Use the specific assignUserToGroup function for better email handling
            await assignUserToGroup(userId, groupType, new Date().toISOString());

            setUsers(prev => prev.filter(user => user._id !== userId));

            setSnackbar({
                open: true,
                message: t('groupAssignment.assignmentSuccess'),
                severity: 'success'
            });

            setConfirmDialog({ open: false, userId: null, groupType: null, userName: '' });
            setSelectedUsers(prev => prev.filter(id => id !== userId));
        } catch (error) {
            console.error('Error assigning group:', error);
            setSnackbar({
                open: true,
                message: t('groupAssignment.assignmentError'),
                severity: 'error'
            });
        }
    };

    const handleBulkAssignment = async (groupType) => {
        if (selectedUsers.length === 0) {
            setSnackbar({
                open: true,
                message: t('groupAssignment.noUsersSelected'),
                severity: 'warning'
            });
            return;
        }

        try {
            const promises = selectedUsers.map(userId =>
                assignUserToGroup(userId, groupType, new Date().toISOString())
            );

            await Promise.all(promises);

            setUsers(prev => prev.filter(user => !selectedUsers.includes(user._id)));
            setSelectedUsers([]);

            setSnackbar({
                open: true,
                message: t('groupAssignment.bulkAssignmentSuccess'),
                severity: 'success'
            });
        } catch (error) {
            console.error('Error in bulk assignment:', error);
            setSnackbar({
                open: true,
                message: t('groupAssignment.bulkAssignmentError'),
                severity: 'error'
            });
        }
    };

    const toggleUserSelection = (userId) => {
        setSelectedUsers(prev => {
            if (prev.includes(userId)) {
                return prev.filter(id => id !== userId);
            }
            return [...prev, userId];
        });
    };

    const selectAllUsers = () => {
        if (selectedUsers.length === filteredUsers.length) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(filteredUsers.map(user => user._id));
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ padding: 3 }}>
            <Card elevation={0} sx={{ mb: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <GroupAddIcon sx={{ fontSize: 32, color: '#1976d2' }} />
                            <Typography variant="h4" component="h1" fontWeight="bold">
                                {t('groupAssignment.title')}
                            </Typography>
                        </Box>
                        <Tooltip title={t('groupAssignment.refresh')}>
                            <IconButton onClick={fetchUsers} color="primary">
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <Alert severity="info" sx={{ mb: 3 }}>
                        {t('groupAssignment.description')}
                    </Alert>

                    <Box display="flex" gap={2} mb={3} alignItems="center" flexWrap="wrap">
                        <TextField
                            placeholder={t('groupAssignment.searchPlaceholder')}
                            variant="outlined"
                            size="small"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{ flex: 1, maxWidth: 400 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showOldUsers}
                                    onChange={(e) => setShowOldUsers(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={t('groupAssignment.showOldUsers')}
                        />

                        <Box display="flex" alignItems="center" gap={2}>
                            <Chip
                                icon={<PendingIcon />}
                                label={`${filteredUsers.length} ${t('groupAssignment.pendingUsers')}`}
                                color="warning"
                                variant="outlined"
                            />

                            {selectedUsers.length > 0 && (
                                <>
                                    <Chip
                                        icon={<CheckCircleIcon />}
                                        label={`${selectedUsers.length} ${t('groupAssignment.selected')}`}
                                        color="primary"
                                    />
                                    <Box display="flex" gap={1}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => handleBulkAssignment('control')}
                                            sx={{ bgcolor: '#4caf50' }}
                                        >
                                            {t('groupAssignment.assignToControl')}
                                        </Button>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => handleBulkAssignment('experimental')}
                                            sx={{ bgcolor: '#ff9800' }}
                                        >
                                            {t('groupAssignment.assignToExperimental')}
                                        </Button>
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Box>

                    {filteredUsers.length === 0 ? (
                        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                            <Typography variant="h6" color="textSecondary">
                                {searchQuery ? t('groupAssignment.noSearchResults') : t('groupAssignment.noUnassignedUsers')}
                            </Typography>
                        </Paper>
                    ) : (
                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
                            <Table>
                                <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Button
                                                size="small"
                                                onClick={selectAllUsers}
                                            >
                                                {selectedUsers.length === filteredUsers.length
                                                    ? t('groupAssignment.deselectAll')
                                                    : t('groupAssignment.selectAll')}
                                            </Button>
                                        </TableCell>
                                        <TableCell>{t('groupAssignment.registrationDate')}</TableCell>
                                        <TableCell>{t('groupAssignment.name')}</TableCell>
                                        <TableCell>{t('groupAssignment.email')}</TableCell>
                                        <TableCell>{t('groupAssignment.status')}</TableCell>
                                        <TableCell align="center">{t('groupAssignment.actions')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredUsers.map((user) => (
                                        <TableRow
                                            key={user._id}
                                            hover
                                            selected={selectedUsers.includes(user._id)}
                                        >
                                            <TableCell padding="checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedUsers.includes(user._id)}
                                                    onChange={() => toggleUserSelection(user._id)}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {formatDate(user.createdAt)}
                                            </TableCell>
                                            <TableCell>
                                                <Typography fontWeight="medium">
                                                    {user.firstname} {user.lastname}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <EmailIcon fontSize="small" color="action" />
                                                    {user.email}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={t('groupAssignment.awaitingAssignment')}
                                                    color="warning"
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Box display="flex" gap={1} justifyContent="center">
                                                    <Tooltip title={t('groupAssignment.controlGroupTooltip')}>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => handleAssignGroup(user._id, 'control', `${user.firstname} ${user.lastname}`)}
                                                            color="success"
                                                            startIcon={<GroupAddIcon />}
                                                        >
                                                            {t('groupAssignment.controlGroup')}
                                                        </Button>
                                                    </Tooltip>
                                                    <Tooltip title={t('groupAssignment.experimentalGroupTooltip')}>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => handleAssignGroup(user._id, 'experimental', `${user.firstname} ${user.lastname}`)}
                                                            color="warning"
                                                            startIcon={<ScienceIcon />}
                                                        >
                                                            {t('groupAssignment.experimentalGroup')}
                                                        </Button>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            <Dialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog({ open: false, userId: null, groupType: null, userName: '' })}
            >
                <DialogTitle>{t('groupAssignment.confirmTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('groupAssignment.confirmMessage', {
                            userName: confirmDialog.userName,
                            groupType: confirmDialog.groupType === 'control'
                                ? t('groupAssignment.controlGroup')
                                : t('groupAssignment.experimentalGroup')
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog({ open: false, userId: null, groupType: null, userName: '' })}>
                        {t('common.cancel')}
                    </Button>
                    <Button onClick={confirmAssignment} variant="contained" autoFocus>
                        {t('common.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default GroupAssignment;